import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { resolveSectorPalette } from '../styles/themeTokens';

const AppContext = createContext();
const SETUP_GUIDE_DISMISSED_STORAGE_KEY = 'alphamizer_setup_guide_dismissed';

const createInitialSetupGuideState = () => ({
  step: null,
  targetTab: null,
  hasWatchlist: false,
  hasPortfolio: false,
  hasPosSizing: false,
  watchlistUploadInteracted: false,
  portfolioImportInteracted: false,
});

const getInitialSetupGuideDismissed = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(SETUP_GUIDE_DISMISSED_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
};

/**
 * `sectorPaletteOverride` + its setter live in `App.jsx` alongside the other
 * appearance-preference state, so the whole slice is persisted through one
 * hydrated snapshot + one debounced write. This provider just receives those
 * values via props and exposes them (plus the derived `sectorPalette`) via
 * context so PosSizingScatterPlot and friends deep in the tree can read them
 * without extra prop-drilling.
 */
export const AppContextProvider = ({
  children,
  sectorPaletteOverride = null,
  setSectorPaletteOverride = null,
}) => {
  const [modifiedOptimizerTickerData, setModifiedOptimizerTickerData] = useState(null);
  const [originalTickerData, setOriginalTickerData] = useState(null);
  const [modifiedOptimizerCurrentTickerData, setModifiedOptimizerCurrentTickerData] = useState(null);
  const [originalCurrentTickerData, setOriginalCurrentTickerData] = useState(null);
  const [isOriginalCurrentTickerDataPopulated, setIsOriginalCurrentTickerDataPopulated ] = useState(false);
  const [aiIdioVolStatus, setAiIdioVolStatus] = useState(null);
  const [setupGuideState, setSetupGuideState] = useState(createInitialSetupGuideState);
  const [setupGuideDismissed, setSetupGuideDismissed] = useState(getInitialSetupGuideDismissed);

  const effectiveSectorPaletteOverride = useMemo(
    () => (sectorPaletteOverride && typeof sectorPaletteOverride === 'object'
      ? sectorPaletteOverride
      : {}),
    [sectorPaletteOverride],
  );
  const sectorPalette = useMemo(
    () => resolveSectorPalette(effectiveSectorPaletteOverride),
    [effectiveSectorPaletteOverride],
  );

  const setSectorColor = useCallback((index, hex) => {
    if (typeof setSectorPaletteOverride !== 'function') return;
    setSectorPaletteOverride((current) => ({ ...(current || {}), [index]: hex }));
  }, [setSectorPaletteOverride]);

  const resetSectorColor = useCallback((index) => {
    if (typeof setSectorPaletteOverride !== 'function') return;
    setSectorPaletteOverride((current) => {
      const source = current || {};
      if (!(index in source)) {
        return source;
      }
      const next = { ...source };
      delete next[index];
      return next;
    });
  }, [setSectorPaletteOverride]);

  const resetSectorPalette = useCallback(() => {
    if (typeof setSectorPaletteOverride !== 'function') return;
    setSectorPaletteOverride({});
  }, [setSectorPaletteOverride]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (setupGuideDismissed) {
        window.localStorage.setItem(SETUP_GUIDE_DISMISSED_STORAGE_KEY, '1');
      } else {
        window.localStorage.removeItem(SETUP_GUIDE_DISMISSED_STORAGE_KEY);
      }
    } catch (_) {
      // Ignore storage write errors; onboarding state still works in-memory.
    }
  }, [setupGuideDismissed]);

  const updateSetupGuideState = useCallback((updates = {}) => {
    setSetupGuideState((current) => {
      const next = {
        ...current,
        ...updates,
      };
      const changed = Object.keys(next).some((key) => next[key] !== current[key]);
      return changed ? next : current;
    });
  }, []);

  const resetSetupGuideState = useCallback(() => {
    setSetupGuideState(createInitialSetupGuideState());
  }, []);

  const dismissSetupGuide = useCallback(() => {
    setSetupGuideDismissed(true);
  }, []);

  const resumeSetupGuide = useCallback(() => {
    setSetupGuideDismissed(false);
  }, []);

  return (
    <AppContext.Provider
      value={{
        modifiedOptimizerTickerData, setModifiedOptimizerTickerData,
        originalTickerData, setOriginalTickerData,
        modifiedOptimizerCurrentTickerData, setModifiedOptimizerCurrentTickerData,
        originalCurrentTickerData, setOriginalCurrentTickerData,
        isOriginalCurrentTickerDataPopulated, setIsOriginalCurrentTickerDataPopulated,
        aiIdioVolStatus, setAiIdioVolStatus,
        setupGuideState, updateSetupGuideState, resetSetupGuideState,
        setupGuideDismissed, dismissSetupGuide, resumeSetupGuide,
        sectorPalette,
        sectorPaletteOverride: effectiveSectorPaletteOverride,
        setSectorColor, resetSectorColor, resetSectorPalette,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
